import React, { Fragment, useEffect, useMemo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    CardHeader,
    IconButton,
    Link,
    Tooltip,
    TooltipProps,
    Typography,
    styled,
} from '@mui/material';
import {
    DataGridPremium,
    GridCellParams,
    GridColDef,
    GridPaginationModel,
} from '@mui/x-data-grid-premium';
import Icon from '@mdi/react';
import { mdiHelpCircleOutline } from '@mdi/js';

import { useAppDispatch, useAppSelector } from '../../state/hooks';
import { fetchMPHPickupParties, updateMPHPickupPartiesApiParams } from '../../state/features/networkHealthSlice';
import { privateRoutes } from '../../routes/appRouteList';
import { Shipper } from '../../interfaces/services/shippers';
import { ApiStatus, Grade } from '../../utils/enums';
import { buildShipperHealthLink } from '../../utils/navigationHelpers';
import { formatNumber } from '../../utils/formattingUtils';
import { getGradeColor } from '../../utils/styleUtils';
import GradeLabel from '../Labels/GradeLabel';

const classesPrefix = 'mphCard';

const classes = {
    cardHeader: `${classesPrefix}-cardHeader`,
    cardHeaderContent: `${classesPrefix}-cardHeaderContent`,
    cardContent: `${classesPrefix}-cardContent`,
    tableWrapper: `${classesPrefix}-tableWrapper`,
    helpText: `${classesPrefix}-helpText`,
    title: `${classesPrefix}-title`,
};

const StyledCard = styled(Card)(({ theme }) => {
    return {
        [`& .${classes.cardHeader}`]: {
            paddingBottom: theme.spacing(0),
            '&:hover': {
                cursor: 'pointer',
                backgroundColor: theme.palette.action.hover,
            },
        },
        [`& .${classes.cardHeaderContent}`]: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        [`& .${classes.cardContent}`]: {
            paddingTop: theme.spacing(0),
        },
        [`& .${classes.tableWrapper}`]: {
            height: '300px',
            '& .mphTable.gradeA': {
                color: getGradeColor(Grade.A, theme),
            },
            '& .mphTable.gradeB': {
                color: getGradeColor(Grade.B, theme),
            },
            '& .mphTable.gradeC': {
                color: getGradeColor(Grade.C, theme),
            },
            '& .mphTable.gradeD': {
                color: getGradeColor(Grade.D, theme),
            },
            '& .mphTable.gradeE': {
                color: getGradeColor(Grade.E, theme),
            },
        },
    };
});

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => {
    return <Tooltip {...props} classes={{ popper: className }} />;
})(() => {
    return {
        [`& .${classes.helpText}`]: {
            fontSize: '0.75rem',
        },
        [`& .${classes.title}`]: {
            fontWeight: 700,
        },
    };
});

const MPHCard = (): React.JSX.Element => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const mphPickupPartiesApiStatus = useAppSelector((state) => { return state.networkHealth.mphPickupPartiesApiStatus; });
    const mphPickupPartiesApiParams = useAppSelector((state) => { return state.networkHealth.mphPickupPartiesApiParams; });
    const mphPickupParties = useAppSelector((state) => { return state.networkHealth.mphPickupParties; });

    useEffect(() => {
        dispatch(fetchMPHPickupParties(mphPickupPartiesApiParams));
    }, [dispatch, mphPickupPartiesApiParams]);

    const columns = useMemo((): GridColDef<Shipper>[] => {
        return [
            {
                headerName: 'Shipper',
                field: 'pickup_party_name',
                sortable: false,
                hideSortIcons: true,
                flex: 1,
                renderCell: ({ row }): React.JSX.Element => {
                    return (
                        <Tooltip title={row.pickup_party_name}>
                            <Link
                                component={RouterLink}
                                to={buildShipperHealthLink(row.pickup_party_id)}
                            >
                                {row.pickup_party_name}
                            </Link>
                        </Tooltip>
                    );
                },
            },
            {
                headerName: 'Sch. MPH',
                description: 'Scheduled Miles Per Hour',
                field: 'average_scheduled_miles_per_hour',
                sortable: false,
                hideSortIcons: true,
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <Typography variant='body2' gutterBottom className={classes.helpText}>
                                    <span className={classes.title}>Scheduled Miles Per Hour</span>
                                    <span> is the average expected velocity needed to deliver a load given the early pickup time to the early delivery time.</span>
                                </Typography>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
            },
            {
                headerName: 'Actual MPH',
                description: 'Actual Miles Per Hour',
                field: 'average_actual_miles_per_hour',
                sortable: false,
                hideSortIcons: true,
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <Typography variant='body2' gutterBottom className={classes.helpText}>
                                    <span className={classes.title}>Actual Miles Per Hour</span>
                                    <span> is the average velocity of orders from departing the pickup location to arriving at the delivery location.</span>
                                </Typography>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
            },
            {
                headerName: 'Sch. vs Act.',
                description: 'Scheduled versus Actual Miles Per Hour',
                field: 'average_scheduled_vs_actual_miles_per_hour',
                sortable: false,
                hideSortIcons: true,
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <div>
                                    <Typography variant='body2' gutterBottom className={classes.helpText}>
                                        <span className={classes.title}>Scheduled vs Actual Miles Per Hour</span>
                                        <span> evaluates the difference between Scheduled MPH and Actual MPH where a quotient of 1 is presumed to be delivering on time.</span>
                                    </Typography>
                                    <Typography variant='body2' className={classes.helpText}>
                                        The grade thresholds for Scheduled vs Actual MPH are dynamically calculated based on the median distance from 1, but all ratios of less than 1 receive an E:
                                    </Typography>
                                    <ul>
                                        <li>
                                            <span className={classes.title}>A</span>
                                            <span> = 2 or more median deviations over 1</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>B</span>
                                            <span> = 1 to 2 median deviations over 1</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>D</span>
                                            <span> = Between 1 and 1 median deviation over 1</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>E</span>
                                            <span> = Actual MPH &#247; Scheduled MPH &lt; 1; or Actual MPH or Scheduled MPH &gt; 75</span>
                                        </li>
                                    </ul>
                                </div>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
                cellClassName: (params: GridCellParams<Shipper, number>): string => {
                    return `mphTable grade${params.row.average_scheduled_vs_actual_miles_per_hour_grade}`;
                },
            },
        ];
    }, []);

    const handlePaginationModelChange = (model: GridPaginationModel): void => {
        dispatch(updateMPHPickupPartiesApiParams({
            // MUI has 0 based paging, and our API is 1 based, so we need to add a 1 here.
            pageNumber: model.page + 1,
            pageSize: model.pageSize,
        }));
    };

    const handleMphCardClick = (): void => {
        navigate(privateRoutes.shipperHealth);
    };

    return (
        <StyledCard>
            <CardHeader
                classes={{
                    root: classes.cardHeader,
                    content: classes.cardHeaderContent,
                }}
                title={
                    <Fragment>
                        <Typography variant='h4'>MPH</Typography>
                        <GradeLabel
                            apiStatus={mphPickupPartiesApiStatus}
                            grade={mphPickupParties.pickup_party_health.scheduled_vs_actual_miles_per_hour_grade}
                            variant='h2'
                        />
                    </Fragment>
                }
                disableTypography
                action={
                    <StyledTooltip
                        title={
                            <div>
                                <Typography variant='body2' gutterBottom className={classes.helpText}>
                                    <span className={classes.title}>Scheduled Miles Per Hour</span>
                                    <span> is the average expected velocity needed to deliver a load given the early pickup time to the early delivery time.</span>
                                </Typography>
                                <Typography variant='body2' gutterBottom className={classes.helpText}>
                                    <span className={classes.title}>Actual Miles Per Hour</span>
                                    <span> is the average velocity of orders from departing the pickup location to arriving at the delivery location.</span>
                                </Typography>
                                <Typography variant='body2' gutterBottom className={classes.helpText}>
                                    <span className={classes.title}>Scheduled vs Actual Miles Per Hour</span>
                                    <span> evaluates the difference between Scheduled MPH and Actual MPH where a quotient of 1 is presumed to be delivering on time.</span>
                                </Typography>
                                <Typography variant='body2' className={classes.helpText}>
                                    The grade thresholds for Scheduled vs Actual MPH are dynamically calculated based on the median distance from 1, but all ratios of less than 1 receive an E:
                                </Typography>
                                <ul>
                                    <li>
                                        <span className={classes.title}>A</span>
                                        <span> = 2 or more median deviations over 1</span>
                                    </li>
                                    <li>
                                        <span className={classes.title}>B</span>
                                        <span> = 1 to 2 median deviations over 1</span>
                                    </li>
                                    <li>
                                        <span className={classes.title}>D</span>
                                        <span> = Between 1 and 1 median deviation over 1</span>
                                    </li>
                                    <li>
                                        <span className={classes.title}>E</span>
                                        <span> = Actual MPH &#247; Scheduled MPH &lt; 1; or Actual MPH or Scheduled MPH &gt; 75</span>
                                    </li>
                                </ul>
                            </div>
                        }
                    >
                        <IconButton size='small' color='primary'>
                            <Icon path={mdiHelpCircleOutline} size='1rem' />
                        </IconButton>
                    </StyledTooltip>
                }
                onClick={handleMphCardClick}
            />

            <CardContent className={classes.cardContent}>
                <div className={classes.tableWrapper}>
                    <DataGridPremium
                        rows={mphPickupParties.pickup_parties}
                        columns={columns}
                        density='compact'
                        disableRowSelectionOnClick
                        disableColumnMenu
                        disableRowGrouping
                        disableAggregation
                        loading={mphPickupPartiesApiStatus === ApiStatus.Idle || mphPickupPartiesApiStatus === ApiStatus.Loading}
                        getRowId={(row: Shipper): string => { return row.pickup_party_id; }}
                        rowCount={mphPickupParties.pickup_party_count}
                        pagination
                        paginationMode='server'
                        paginationModel={{
                            // MUI has 0 based paging, and our API is 1 based, so we need to subtract a 1 here.
                            page: mphPickupPartiesApiParams.pageNumber - 1,
                            pageSize: mphPickupPartiesApiParams.pageSize,
                        }}
                        onPaginationModelChange={handlePaginationModelChange}
                        pageSizeOptions={[5, 10, 25, 50, 100]}
                        initialState={{
                            density: 'compact',
                        }}
                    />
                </div>
            </CardContent>
        </StyledCard>
    );
};

export default MPHCard;
